/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/Skeleton/Skeleton.css';

export interface SkeletonRowProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'size'> {
  fullWidth?: boolean;
  lines?: number;
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
  variant?: 'heading' | 'row' | 'empty';
  multilineGap?: 'xsmall' | 'small' | 'medium' | 'big' | 'xbig';
  children?: string;
}

const SINGLELINE = 1;

export const SkeletonRow: FunctionComponent<SkeletonRowProps> = ({
  className,
  fullWidth = false,
  lines = SINGLELINE,
  size = 'small',
  variant = 'row',
  children = null,
  multilineGap = 'medium',
  ...props
}) => {
  const sharedClasses = [
    [`dropin-skeleton-row__${variant}`, variant],
    [`dropin-skeleton-row__${variant}-${size}`, variant && size],
  ];

  if (!children && variant === 'empty') {
    return (
      <div
        className={classes([
          'dropin-skeleton-row dropin-skeleton-row__empty',
          className,
        ])}
      />
    );
  }

  if (children) {
    const svg = children.trim();
    return (
      <div
        {...props}
        class={classes([
          'dropin-skeleton-row',
          [`dropin-skeleton-row--full`, fullWidth],
          className,
        ])}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }

  const isMultiline = lines > SINGLELINE;

  if (isMultiline === false) {
    return (
      <div
        {...props}
        class={classes([
          'dropin-skeleton-row',
          [`dropin-skeleton-row--full`, fullWidth],
          'dropin-skeleton--row__content',
          ...sharedClasses,
          className,
        ])}
      />
    );
  }

  return (
    <div
      {...props}
      style={{ '--multiline-gap-spacing': `var(--spacing-${multilineGap})` }}
      class={classes([
        'dropin-skeleton-row--multiline',
        [`dropin-skeleton-row--full`, fullWidth],
        className,
      ])}
    >
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          class={classes([
            'dropin-skeleton-row',
            [`dropin-skeleton-row--full`, fullWidth],
            'dropin-skeleton--row__content',
            ...sharedClasses,
          ])}
        />
      ))}
    </div>
  );
};

export interface SkeletonProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'size'> {
  rowGap?: 'xsmall' | 'small' | 'medium' | 'big' | 'xbig';
}

export const Skeleton: FunctionComponent<SkeletonProps> = ({
  className,
  children,
  rowGap = 'medium',
  ...props
}) => {
  return (
    <div
      style={{ '--row-gap-spacing': `var(--spacing-${rowGap})` }}
      {...props}
      className={classes(['dropin-skeleton', className])}
      role="status"
      aria-label="Loading..."
    >
      {children}
    </div>
  );
};
